import { AlertModal, Box, Button, Modal } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import style from "./audit-log-dialog.scss";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { DeletionReport } from "domain/auditLog";
import { deletionReportsService, extractFilename } from "services/audit-log/AuditLogService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { formatTimestamp } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    report?: DeletionReport;
    onClose: () => void;
}

function DeletionReportDetailDialog(props: Props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [errorModal, setErrorModal] = React.useState({ isOpen: false, title: "" });
    const downloadAbortControllerRef = React.useRef<AbortController>();

    React.useEffect(() => {
        return () => downloadAbortControllerRef.current?.abort();
    }, []);

    const handleDownload = () => {
        if (!props.report) {
            return;
        }
        setIsDownloading(true);
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.DOWNLOAD_DELETION_REPORT });
        const abortController = new AbortController();
        downloadAbortControllerRef.current = abortController;
        deletionReportsService
            .downloadDeletionReport(props.report.uuid, abortController)
            .then((response) => {
                const filename = extractFilename(response.headers.get("Content-Disposition"));
                return response.blob().then((blob) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", filename);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("AuditLogs.deletionReports.downloadSuccess", { uuid: props.report?.uuid })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("AuditLogs.deletionReports.downloadFailed") });
            })
            .finally(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setIsDownloading(false);
            });
    };

    const report = props.report;

    const fields: { label: string; value: string | number | React.ReactNode; isList?: boolean }[] = report
        ? [
              { label: t("AuditLogs.deletionReports.detail.username"), value: report.username },
              { label: t("AuditLogs.deletionReports.detail.logCount"), value: report.logCount },
              {
                  label: t("AuditLogs.deletionReports.detail.creationTime"),
                  value: formatTimestamp(report.creationTime),
              },
              {
                  label: t("AuditLogs.deletionReports.detail.dateRange"),
                  value: report.dateRange
                      .split(" - ")
                      .map((d) => formatTimestamp(d))
                      .join(" - "),
              },
              {
                  label: t("AuditLogs.deletionReports.detail.eventTypes"),
                  isList: true,
                  value: (
                      <ul>
                          {report.eventTypes.map((type) => (
                              <li key={type}>{type}</li>
                          ))}
                      </ul>
                  ),
              },
          ]
        : [];

    return (
        <>
            <Modal
                isOpen={!!report}
                hideModal={props.onClose}
                title={report && t("AuditLogs.detailViewTitle", { uuid: report.uuid })}
                width="Lg"
                className={style.deletionReportDialog}
                testId={testIds.workArea.auditLogs.deletionReports.detailDialog.itself}
                dataType="DELETION_REPORT_DETAIL"
                buttons={
                    <Button
                        variant="Primary"
                        onClick={handleDownload}
                        disabled={isDownloading}
                        testId={testIds.workArea.auditLogs.deletionReports.detailDialog.downloadButton}
                    >
                        {t("AuditLogs.deletionReports.downloadAsCsv")}
                    </Button>
                }
            >
                <div className={style.scrollableContent}>
                    {fields.map(({ label, value, isList }) => (
                        <Box
                            key={label}
                            gap="Xs"
                            paddingTop="Xs"
                            paddingBottom="Xs"
                            columnLayout={isList}
                            className={isList ? undefined : style.fieldRow}
                        >
                            <span className={style.label}>{label}</span>
                            <span className={style.value}>{value ?? t("Common.na")}</span>
                        </Box>
                    ))}
                </div>
            </Modal>
            <AlertModal
                title={errorModal.title}
                variant="Warning"
                isOpen={errorModal.isOpen}
                hideModal={() => setErrorModal({ isOpen: false, title: "" })}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setErrorModal({ isOpen: false, title: "" })}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <Box marginTop="Sm">
                    {t("Common.genericErrorMessage")}
                    {t("Common.genericContactSupport")}
                </Box>
            </AlertModal>
        </>
    );
}

export default DeletionReportDetailDialog;
